import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Menu, Clapperboard, Loader2, Heart, Download, Trash2, RectangleHorizontal, RectangleVertical, Square, MonitorPlay, X, ImagePlay } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, videoStreamUrl, favoriteImageUrl } from "@/lib/api";
import { useVideoJobs } from "@/context/VideoJobsContext";

const MODELS = [
  { id: "sora-2", name: "Sora 2", tag: "Standard quality, faster" },
  { id: "sora-2-pro", name: "Sora 2 Pro", tag: "Highest fidelity, slower" },
];
const SIZES = [
  { id: "1280x720", label: "Landscape", icon: RectangleHorizontal },
  { id: "1792x1024", label: "Wide", icon: MonitorPlay },
  { id: "1024x1792", label: "Portrait", icon: RectangleVertical },
  { id: "1024x1024", label: "Square", icon: Square },
];
const DURATIONS = [4, 8, 12];

const Chip = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-sm transition-colors ${active ? "bg-indigo-500 border-indigo-500 text-white" : "bg-[#11131E] border-slate-800 text-slate-300 hover:border-indigo-500/40 hover:text-white"}`}>
    {children}
  </button>
);

export default function StudioPage({ onOpenSidebar }) {
  const [params, setParams] = useSearchParams();
  const { jobs, addJob, updateJob, removeJob } = useVideoJobs();
  const [prompt, setPrompt] = useState("");
  const [model, setModel] = useState("sora-2");
  const [size, setSize] = useState("1280x720");
  const [duration, setDuration] = useState(4);
  const [reference, setReference] = useState(null);
  const [busy, setBusy] = useState(false);

  const fromId = params.get("from");
  useEffect(() => {
    if (!fromId) { setReference(null); return; }
    api.get("/favorites").then((r) => {
      const f = r.data.find((x) => x.id === fromId && x.kind === "image");
      if (!f) { toast.error("That image is no longer in your gallery"); setParams({}); return; }
      setReference(f);
      setPrompt((p) => p || `Bring this scene to life: ${f.prompt}`.slice(0, 500));
    }).catch(() => setParams({}));
  }, [fromId, setParams]);

  const clearReference = () => setParams({});

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/videos", { prompt: prompt.trim(), model, size, duration, reference_favorite_id: reference?.id || undefined });
      addJob(data);
      setPrompt("");
      toast.success("Rendering started — we'll notify you when it's ready");
      if (reference) setParams({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const pin = async (job) => {
    try {
      const { data } = await api.post(`/videos/${job.id}/pin`);
      updateJob(job.id, { favorite_id: data.favorite_id });
      toast.success("Video pinned to your gallery");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (job) => {
    try {
      await api.delete(`/videos/${job.id}`);
      removeJob(job.id);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="flex-1 overflow-y-auto relative" data-testid="studio-view">
      <div className="absolute inset-x-0 top-0 h-[360px] grid-bg pointer-events-none" />
      <header className="sticky top-0 z-10 h-16 flex items-center px-4 lg:px-10 bg-[#090A0F]/70 backdrop-blur-xl border-b border-slate-800/60">
        <button onClick={onOpenSidebar} className="lg:hidden mr-3 text-slate-400 hover:text-white" data-testid="open-sidebar-btn"><Menu className="w-5 h-5" /></button>
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400">Video studio</p>
      </header>

      <div className="relative max-w-5xl mx-auto px-4 lg:px-10 py-10">
        <div className="rise">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-100">Describe a scene.<br className="hidden sm:block" /> Get a film clip.</h1>
          <p className="mt-4 text-sm sm:text-base text-slate-400 max-w-xl leading-relaxed">Powered by Sora 2. Videos take a few minutes to render and are shown once — pin the keepers to your gallery.</p>
        </div>

        <div className="mt-8 p-5 rounded-2xl bg-[#11131E] border border-slate-800/80 space-y-5" data-testid="studio-form">
          {reference && (
            <div className="flex items-center gap-4 p-3 rounded-xl bg-[#0D0F18] border border-indigo-500/30" data-testid="studio-reference">
              <img src={favoriteImageUrl(reference.id)} alt="" className="w-20 h-14 rounded-lg object-cover" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 flex items-center gap-1.5"><ImagePlay className="w-3.5 h-3.5" /> Animating gallery image</p>
                <p className="text-sm text-slate-300 truncate mt-0.5">{reference.prompt}</p>
                <p className="text-[11px] text-slate-500">The clip will start from this frame (auto-cropped to the chosen format).</p>
              </div>
              <button onClick={clearReference} data-testid="studio-reference-remove" className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-[#1F2338]" aria-label="Remove reference"><X className="w-4 h-4" /></button>
            </div>
          )}
          <textarea data-testid="video-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
            placeholder={reference ? "Describe the motion: slow camera push-in, leaves drifting, gentle wind…" : "A drone shot gliding over a misty pine forest at sunrise, cinematic, 35mm film look"}
            className="w-full bg-[#0D0F18] border border-slate-800 focus:border-indigo-500/60 outline-none rounded-xl px-4 py-3 text-[15px] text-slate-100 placeholder:text-slate-600 resize-none" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Model</p>
              <div className="flex flex-wrap gap-2">
                {MODELS.map((m) => <Chip key={m.id} active={model === m.id} onClick={() => setModel(m.id)} testId={`video-model-${m.id}`}>{m.name}</Chip>)}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">{MODELS.find((m) => m.id === model)?.tag}</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Format</p>
              <div className="flex flex-wrap gap-2">
                {SIZES.map((s) => { const I = s.icon; return <Chip key={s.id} active={size === s.id} onClick={() => setSize(s.id)} testId={`video-size-${s.id}`}><I className="w-3.5 h-3.5" /> {s.label}</Chip>; })}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Duration</p>
              <div className="flex flex-wrap gap-2">
                {DURATIONS.map((d) => <Chip key={d} active={duration === d} onClick={() => setDuration(d)} testId={`video-duration-${d}`}>{d}s</Chip>)}
              </div>
            </div>
          </div>

          <button onClick={generate} disabled={busy || !prompt.trim()} data-testid="video-generate-btn"
            className="inline-flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 disabled:opacity-50 text-white font-medium px-6 py-3 rounded-xl transition-colors">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : reference ? <ImagePlay className="w-4 h-4" /> : <Clapperboard className="w-4 h-4" />} {reference ? "Animate image" : "Generate video"}
          </button>
        </div>

        <div className="mt-10 space-y-4" data-testid="video-job-list">
          {jobs && jobs.length === 0 && <p className="text-sm text-slate-500" data-testid="video-empty">No renders yet. Your videos will appear here.</p>}
          {jobs?.map((job) => <VideoCard key={job.id} job={job} onPin={pin} onDelete={remove} />)}
        </div>
      </div>
    </div>
  );
}

const VideoCard = ({ job, onPin, onDelete }) => {
  const running = job.status === "queued" || job.status === "running";
  const url = videoStreamUrl(job.id);
  return (
    <div className="rise rounded-2xl bg-[#141726] border border-slate-800 overflow-hidden" data-testid={`video-job-${job.id}`}>
      <div className="flex items-start justify-between gap-4 px-5 py-4">
        <div className="flex items-start gap-3 min-w-0">
          {job.reference_favorite_id && <img src={favoriteImageUrl(job.reference_favorite_id)} alt="" className="w-14 h-10 rounded-md object-cover shrink-0 border border-slate-800" data-testid={`video-reference-thumb-${job.id}`} />}
          <div className="min-w-0">
            <p className="text-slate-100 text-sm leading-snug">{job.prompt}</p>
            <p className="mt-1.5 text-[11px] text-slate-500 flex flex-wrap items-center gap-2">
              <span className="px-2 py-0.5 rounded-full border bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{job.model === "sora-2-pro" ? "Sora 2 Pro" : "Sora 2"}</span>
              {job.reference_favorite_id && <span className="px-2 py-0.5 rounded-full border bg-indigo-500/10 text-indigo-300 border-indigo-500/30">image → video</span>}
              <span>{job.size}</span><span>·</span><span>{job.duration}s</span>
              {job.elapsed && <><span>·</span><span>rendered in {Math.round(job.elapsed)}s</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {job.available && (
            <>
              <button onClick={() => onPin(job)} disabled={!!job.favorite_id} data-testid={`video-pin-${job.id}`} className={`p-2 rounded-lg transition-colors ${job.favorite_id ? "text-rose-400" : "text-slate-400 hover:text-rose-300 hover:bg-[#1F2338]"}`} aria-label="Pin to gallery" title={job.favorite_id ? "Pinned" : "Pin to gallery"}>
                <Heart className={`w-4 h-4 ${job.favorite_id ? "fill-current" : ""}`} />
              </button>
              <a href={url} download={`omnimodel-${job.id}.mp4`} data-testid={`video-download-${job.id}`} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-[#1F2338] transition-colors" aria-label="Download"><Download className="w-4 h-4" /></a>
            </>
          )}
          {!running && <button onClick={() => onDelete(job)} data-testid={`video-delete-${job.id}`} className="p-2 rounded-lg text-slate-400 hover:text-rose-300 hover:bg-[#1F2338] transition-colors" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>}
        </div>
      </div>
      {running && (
        <div className="px-5 pb-5 flex items-center gap-3" data-testid={`video-status-${job.id}`}>
          <div className="flex gap-1.5"><span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" /></div>
          <span className="text-xs text-slate-400">{job.status === "queued" ? "Queued…" : "Rendering — usually 1–3 minutes. We'll notify you."}</span>
        </div>
      )}
      {job.status === "failed" && <p className="px-5 pb-5 text-sm text-rose-400" data-testid={`video-error-${job.id}`}>{job.error || "Generation failed"}</p>}
      {job.status === "done" && !job.available && <p className="px-5 pb-5 text-xs text-slate-500" data-testid={`video-expired-${job.id}`}>This video is no longer available{job.favorite_id ? " here — find it in your gallery" : " (it was shown once and not pinned)"}.</p>}
      {job.available && (
        <div className="bg-black">
          <video src={url} controls playsInline preload="metadata" className="w-full max-h-[520px] mx-auto" data-testid={`video-player-${job.id}`} />
        </div>
      )}
    </div>
  );
};
